import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Pane,
  Card,
  Heading,
  Text,
  Button,
  Icon,
  StarIcon,
  PeopleIcon,
  BriefcaseIcon,
  SettingsIcon,
  SnowflakeIcon,
  ShieldIcon,
  CameraIcon,
  MapIcon,
  DashboardIcon,
  ChatIcon,
} from "evergreen-ui";
import {
  FaGasPump,
  FaMapMarkedAlt,
  FaUsb,
  FaBluetooth,
  FaTv,
} from "react-icons/fa";
import "./CarHire.css";
import AOS from "aos";
import "aos/dist/aos.css";

const CarCard = ({ car }) => {
  const handleWhatsAppClick = () => {
    const phoneNumber = "+254117670542";
    const message = `Hello, I am interested in hiring the vehicle with ID: ${car.id} and Name: ${car.car_name}. Could you please provide more details and availability?`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappURL, "_blank");
  };

  useEffect(() => {
      AOS.init();
      AOS.refresh();
      window.scrollTo(0,0);
    }, []);

  return (
    <Card
      elevation={1}
      hoverElevation={2}
      width={340}
      padding={16}
      display="flex"
      flexDirection="column"
      borderRadius={8}
      className="car-card"
    >
      {car.image && (
        <img
          src={`${process.env.REACT_APP_BASE_URL}/${car.image}`}
          alt={car.car_name || "Car Image"}
          className="car-image"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/placeholder-car.jpg";
          }}
        />
      )}
      <Pane display="flex" flexDirection="column" gap={12}>
        <Heading size={600}>{car.car_name}</Heading>
        <Pane display="flex" alignItems="center" gap={4}>
          <Icon icon={StarIcon} color="warning" size={14} />
          <Icon icon={StarIcon} color="warning" size={14} />
          <Icon icon={StarIcon} color="warning" size={14} />
          <Icon icon={StarIcon} color="warning" size={14} />
          <Icon icon={StarIcon} color="warning" size={14} half />
          <Text size={300} color="muted">
            (1k Reviews)
          </Text>
        </Pane>
        <Pane display="flex" flexWrap="wrap" gap={12}>
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <Icon icon={PeopleIcon} size={16} />
            {car.seats || "N/A"}-Seater
          </Text>
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <Icon icon={BriefcaseIcon} size={16} />
            {car.bags || "N/A"} Large Bags
          </Text>
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <Icon icon={SettingsIcon} size={16} />
            {car.transmission || "N/A"}
          </Text>
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <FaGasPump size={16} />
            {car.fuel || "N/A"}
          </Text>
          {car.air_conditioning && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <Icon icon={SnowflakeIcon} size={16} />
              Air Conditioning
            </Text>
          )}
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <Icon icon={ShieldIcon} size={16} />
            {car.airbags || "N/A"}
          </Text>
          {car.reverse_camera && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <Icon icon={CameraIcon} size={16} />
              Reverse Camera
            </Text>
          )}
          {car.lane_assist && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <Icon icon={MapIcon} size={16} />
              Lane Assist
            </Text>
          )}
          {car.gps && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <FaMapMarkedAlt size={16} />
              GPS Navigation
            </Text>
          )}
          {car.usb && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <FaUsb size={16} />
              USB Charging
            </Text>
          )}
          {car.bluetooth && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <FaBluetooth size={16} />
              Bluetooth
            </Text>
          )}
          {car.touchscreen && (
            <Text size={400} display="flex" alignItems="center" gap={4}>
              <FaTv size={16} />
              Touchscreen Display
            </Text>
          )}
          <Text size={400} display="flex" alignItems="center" gap={4}>
            <Icon icon={DashboardIcon} size={16} />
            {car.mileage || "N/A"}
          </Text>
        </Pane>
        <Pane
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderTop="1px solid"
          borderColor="lightgray"
          paddingTop={12}
        >
          <Text size={500} fontWeight="bold">
            {car.price ? `KES ${car.price} /day` : "Price N/A"}
          </Text>
          <Button
            appearance="primary"
            intent="success"
            iconBefore={ChatIcon}
            onClick={handleWhatsAppClick}
            height={32}
            as={Link}
            to="#"
          >
            Contact
          </Button>
        </Pane>
      </Pane>
    </Card>
  );
};

const CarHire = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/carhires`);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setCars(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cars:", error);
        setLoading(false);
      }
    };

    fetchCars();
  }, []);

  return (
    <Pane
      padding={20}
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      className="car-section"
    >
      <Pane className="banner-dest">
        <Pane className="banner-sect">
          <p className="banner-title">
            Car Hire
          </p>
        </Pane>
      </Pane>
      <Pane
        display="flex"
        flexDirection="column"
        alignItems="center"
        lineHeight='1px'
        marginTop={32}
        marginBottom={16}
        gap={8}
        className="car-text"
      >
        <p className="title-secondary">
          Latest Inventory
        </p>
        <span className="title-primary">
        Affordable Car Rentals, Anytime
        </span>
      </Pane>
      <Pane
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        marginTop={20}
        gap={16}
        className="car-main"
      >
        {loading ? (
          <Text>Loading...</Text>
        ) : cars.length > 0 ? (
          cars.map((car) => <CarCard key={car.id} car={car} />)
        ) : (
          <Text>No cars available</Text>
        )}
      </Pane>
    </Pane>
  );
};

export default CarHire;